import { useQuery } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useSelector } from 'react-redux'

import { getUnifiedOnboardingStatus } from '@/api/actions/onboarding/onboardingActions'
import type { IUnifiedOnboardingStatusResponse } from '@/api/actions/onboarding/onboarding.types'
import { Queries } from '@/api/queries.enum'
import type { RootState } from '@/store/store'
import { isImpersonationActive } from '@/utils/auth/impersonationSession'

/**
 * React Query hook for unified onboarding status.
 *
 * Caching:
 * - No stale time: we want fresh employee/org status after significant events
 * - Refetch on mount and window focus to keep UI responsive to changes
 *
 * Invalidate on:
 * - Impersonation start/end
 * - Intake survey submission
 */
export const useOnboardingStatus = (enabled = true) => {
    // Scope cache by tenant, user, and impersonation activity to prevent stale crossover
    const { organization, user } = useSelector((s: RootState) => s.auth)
    const orgUuid = organization?.uuid || 'org'
    const userUuid = user?.uuid || 'user'
    const impersonating = isImpersonationActive() ? 'imp' : 'self'

    return useQuery<IUnifiedOnboardingStatusResponse, AxiosError>({
        queryKey: [Queries.onboardingStatus, orgUuid, userUuid, impersonating],
        queryFn: getUnifiedOnboardingStatus,
        enabled,
        retry: 1,
        // Event-driven invalidation: rely on explicit invalidations from key flows
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })
}
